import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerLayout, careerType } from 'services/careerType';

/**
 * Масштаб — оверлеем в правом нижнем углу холста (ТЗ 0071, п. 6.2).
 *
 * Прежде процент, «−» и «+» стояли в панели над картой и спорили за строку с
 * фильтрами. Масштаб — свойство холста, а не отбора: там, где его ищут в
 * любом картографическом интерфейсе, он и стоит.
 *
 * 🔴 Кнопки 44 × 44, а не плотные 28 из панели: оверлей стоит поверх
 * содержимого, и промах мимо него — это клик по карте, который закрывает
 * паспорт или уводит фокус. Подпись у каждой — и `aria-label`, и `title`:
 * символ «−» скринридер читает как «минус», а мышь без подсказки не знает, что
 * значит круговая стрелка.
 *
 * Лежит ВНЕ svg: перетаскивание холста слушает указатель на самом svg, и
 * кнопка внутри него начинала бы панораму вместо нажатия.
 */
interface ISkillMapZoomControls {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
}

export const SkillMapZoomControls = memo(
  ({ zoom, onZoomIn, onZoomOut, onReset }: ISkillMapZoomControls) => {
    const { t } = useTranslation();
    const percent = t('careerCenter.map.zoomLevel', { n: Math.round(zoom * 100) });

    return (
      <Root role="group" aria-label={t('careerCenter.map.zoom')}>
        <Button
          type="button"
          aria-label={t('careerCenter.map.zoomOut')}
          title={t('careerCenter.map.zoomOut')}
          onClick={onZoomOut}>
          −
        </Button>
        <Button
          type="button"
          aria-label={t('careerCenter.map.zoomIn')}
          title={t('careerCenter.map.zoomIn')}
          onClick={onZoomIn}>
          +
        </Button>
        {/* Процент стоит ВНУТРИ кнопки возврата: он отвечает на вопрос «что
            вернётся», а отдельной строкой занимал бы место без действия. */}
        <Button
          type="button"
          $wide
          aria-label={`${t('careerCenter.map.resetView')}, ${percent}`}
          title={t('careerCenter.map.resetView')}
          onClick={onReset}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
            <path
              d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Percent>{percent}</Percent>
        </Button>
      </Root>
    );
  },
);

const Root = styled.div`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background: ${Colors.white};
`;

const Button = styled.button<{ $wide?: boolean }>`
  ${careerType.bodyStrong()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: ${careerLayout.hitTarget}px;
  height: ${careerLayout.hitTarget}px;
  padding: 0 ${({ $wide }) => ($wide ? 10 : 0)}px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${Colors.black};
  cursor: pointer;

  &:hover {
    background: ${Colors.bg_gray};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 1px;
  }
`;

const Percent = styled.span`
  ${careerType.meta()};
  color: ${Colors.gray_dark};
`;
